import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { RecentTrailer } from '../../types/user';
import { Play, Calendar, Clock } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getLocalRecentTrailers } from '../../services/localStorageService';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
`;

const TrailerList = styled.div`
  display: grid;
  gap: var(--spacing-4);
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
`;

const TrailerCard = styled(Link)`
  text-decoration: none;
  color: inherit;
  background: var(--color-background);
  border-radius: var(--border-radius);
  overflow: hidden;
  transition: all 0.2s;
  position: relative;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);

    .play-button {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1.1);
    }
  }
`;

const ThumbnailContainer = styled.div`
  position: relative;
  padding-top: 56.25%; /* 16:9 aspect ratio */
  background: var(--color-background-dark);
`;

const Thumbnail = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
  transition: all 0.2s;
  color: white;
  cursor: pointer;
`;

const TrailerInfo = styled.div`
  padding: var(--spacing-3);
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
`;

const TrailerTitle = styled.h4`
  margin: 0;
  color: var(--color-text-primary);
  font-size: 1rem;
  line-height: 1.4;
`;

const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  color: var(--color-text-secondary);
  font-size: 0.875rem;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-1);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: var(--spacing-6);
  color: var(--color-text-secondary);
`;

interface RecentTrailersProps {
  recentTrailers?: RecentTrailer[];
}

export const RecentTrailers: React.FC<RecentTrailersProps> = ({ recentTrailers: propTrailers }) => {
  const { user } = useSelector((state: RootState) => state.auth);
  
  // Utiliser uniquement les bandes-annonces du profil connecté
  const trailers = user?.recentTrailers || [];

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>, trailer: RecentTrailer) => {
    const target = e.target as HTMLImageElement;
    const videoId = trailer.thumbnail.split('/').pop()?.split('.')[0];
    
    if (videoId) {
      // Si l'image actuelle échoue, essayer avec hqdefault
      if (target.src.includes('maxresdefault')) {
        target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
      }
      // Si hqdefault échoue, utiliser mqdefault
      else if (target.src.includes('hqdefault')) {
        target.src = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
      }
      // Si mqdefault échoue, utiliser sddefault
      else if (target.src.includes('mqdefault')) {
        target.src = `https://img.youtube.com/vi/${videoId}/sddefault.jpg`;
      }
      // Si tout échoue, utiliser l'image par défaut
      else {
        target.src = 'https://placehold.co/500x281?text=Image+non+disponible';
      }
    } else {
      target.src = 'https://placehold.co/500x281?text=Image+non+disponible';
    }
  };

  if (!trailers || trailers.length === 0) {
    return (
      <Container>
        <EmptyState>
          Vous n'avez pas encore regardé de bandes-annonces.
          <br />
          Explorez nos films et séries pour en découvrir !
        </EmptyState>
      </Container>
    );
  }

  // Trier les bandes-annonces par date de visionnage (la plus récente en premier)
  // et limiter à 8 bandes-annonces maximum
  const sortedTrailers = [...trailers]
    .sort((a, b) => {
      try {
        // Vérifier si les dates sont valides
        if (!a?.viewedAt || !b?.viewedAt) {
          return 0;
        }

        let dateA: Date;
        let dateB: Date;

        try {
          dateA = typeof a.viewedAt === 'string' ? parseISO(a.viewedAt) : a.viewedAt;
        } catch (error) {
          console.error('Erreur parsing dateA:', error);
          dateA = new Date(0);
        }

        try {
          dateB = typeof b.viewedAt === 'string' ? parseISO(b.viewedAt) : b.viewedAt;
        } catch (error) {
          console.error('Erreur parsing dateB:', error);
          dateB = new Date(0);
        }

        // Vérifier si les dates sont valides après parsing
        if (!dateA || isNaN(dateA.getTime()) || !dateB || isNaN(dateB.getTime())) {
          return 0;
        }

        return dateB.getTime() - dateA.getTime();
      } catch (error) {
        console.error('Erreur de tri:', error);
        return 0;
      }
    })
    .slice(0, 8); // Limiter à 8 bandes-annonces

  const formatDate = (date: string | Date | undefined) => {
    if (!date) {
      return 'Date inconnue';
    }

    try {
      const parsedDate = typeof date === 'string' ? parseISO(date) : date;
      
      if (!parsedDate || isNaN(parsedDate.getTime())) {
        return 'Date invalide';
      }

      return formatDistanceToNow(parsedDate, {
        addSuffix: true,
        locale: fr
      });
    } catch (error) {
      console.error('Erreur de formatage de date:', error);
      return 'Date inconnue';
    }
  };

  return (
    <Container>
      <TrailerList>
        {sortedTrailers.map((trailer, index) => (
          <TrailerCard 
            key={`${trailer.mediaId || 'unknown'}-${trailer.viewedAt || index}`} 
            to={`/${trailer.mediaType}/${trailer.mediaId}`}
          >
            <ThumbnailContainer>
              <Thumbnail
                src={trailer.thumbnail}
                alt={trailer.mediaTitle}
                onError={(e) => handleImageError(e, trailer)}
              />
              <PlayButton className="play-button">
                <Play size={24} />
              </PlayButton>
            </ThumbnailContainer>
            <TrailerInfo>
              <TrailerTitle>{trailer.mediaTitle}</TrailerTitle>
              <MetaInfo>
                <MetaItem>
                  <Calendar size={14} />
                  {new Date(trailer.releaseDate).getFullYear()}
                </MetaItem>
                <MetaItem>
                  <Clock size={14} />
                  {formatDate(trailer.viewedAt)}
                </MetaItem>
              </MetaInfo>
            </TrailerInfo>
          </TrailerCard>
        ))}
      </TrailerList>
    </Container>
  );
};

export default RecentTrailers;
